import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from './useTenant';

/**
 * The money cycle: what would go on an invoice, issuing it, recording that
 * somebody paid, and who owes what.
 *
 * blueprints/invoices.py is the API. Nothing here re-derives a figure the
 * server already computed — not the totals, not the VAT, not who is
 * overdue. That is deliberate and it is the same rule the backend follows
 * with itself: core/invoicing.py owns the arithmetic so an invoice and the
 * screen that issued it can never disagree about what a load earned.
 *
 * EVERY MUTATION HERE INVALIDATES THE DRAFT. Issuing consumes loads,
 * cancelling releases them back, and a draft left in cache after either one
 * is a list of loads that have moved — the exact failure the server's
 * `invoice_orders_changed` 409 exists to catch, shown to the dispatcher
 * before they press the button rather than after.
 */

/**
 * Caches any invoice write can move. `orders` and `dashboard` ride along
 * because issuing stamps orders.invoice_id / invoice_status and cancelling
 * unstamps them — the orders table renders that column, and leaving it
 * stale shows a load as un-invoiced hours after it was billed.
 *
 * `invoice` (singular) is invalidated without a tenant id so it prefix-
 * matches every open invoice detail, whichever one it is.
 */
// `client` is here for V-86's balance warning, and V-91 is why it had to be
// added rather than assumed: the key was filed under this prefix with a
// comment claiming a payment would refresh it, and nothing invalidated the
// prefix. Measured in a browser — a dispatcher recorded a payment on this
// console's own screen and the order form went on saying "Klientas vėluoja
// apmokėti: €8 420,50 · 47 d." while the server already answered €4 820,50.
// A €3 600 discrepancy on the screen that decides whether to haul more for
// this client.
// 'invoice-compliance' is listed in its own right and not covered by
// 'invoice': React Query prefix-matches ARRAY ELEMENTS, not string prefixes,
// so ['invoice'] matches ['invoice', id] and never ['invoice-compliance', …].
// It has to be here because issuing and cancelling both change the numbering
// the check is about, and a stale "no findings" over a book that has just
// grown a gap is the one wrong answer that panel must never show.
// 'invoice-export-profiles' is deliberately NOT here — it depends on no
// invoice and on no tenant data, so refetching it on every write would be
// pure noise.
const INVOICE_CACHES = ['invoices', 'invoice', 'invoice-compliance', 'receivables',
                        'invoice-draft', 'unbilled', 'orders', 'dashboard', 'worklist',
                        'client'];

function useInvalidateInvoiceCaches() {
  const queryClient = useQueryClient();
  return () => INVOICE_CACHES.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
}

/**
 * GET /api/tenants/<id>/receivables — who owes, and how much of it is late.
 *
 * Read straight through: `clients` arrives already sorted by overdue money
 * first, and re-sorting it here would be a second opinion about what
 * matters most on a screen whose whole point is the first row.
 */
/**
 * Delivered work that never reached an invoice, and what is blocking each
 * load — the money this carrier has already earned and not asked for.
 *
 * The day bound is part of the query key on purpose: changing it asks a
 * different question, and reusing one cache entry across two of them would
 * show yesterday's "stuck over 14 days" answer under a "stuck over 30 days"
 * heading for as long as the entry stayed fresh.
 *
 * Longer staleTime than the receivables above: nothing on this panel moves
 * within a minute. A load becomes unstuck when somebody uploads a waybill or
 * sets a price, and both of those invalidate this key through the mutation
 * that did them.
 */
export function useUnbilled(olderThanDays) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.unbilled(tenantId, olderThanDays),
    queryFn: () => api(`/api/tenants/${tenantId}/unbilled${qs({ older_than_days: olderThanDays })}`),
    enabled: Boolean(tenantId),
    staleTime: 120_000,
  });
}

/**
 * V-74. Generate the delivery notes that failed, now.
 *
 * The failures behind a missing delivery note are the ones a PERSON fixes —
 * a full disk, an unwritable POD_DIR, a missing font — so the carrier who
 * has just freed the disk should not then wait on a five-minute background
 * loop they cannot see. Where that loop is switched off entirely
 * (ARAGELA_POD_RETRY_INTERVAL_SECONDS=0) this is the only way to produce the
 * documents at all.
 *
 * The reply carries order_ids, not a count, and both halves matter: some
 * generated and some still failing is the ordinary outcome of a partly-fixed
 * cause, and the caller has to be able to say which.
 *
 * Invalidates the whole invoicing prefix rather than just this panel: a
 * generated note moves a load out of the no_pod bucket and into the invoice
 * draft, the worklist and the month close, all of which were showing it as
 * stuck a moment ago.
 */
export function useRetryPods() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api(`/api/tenants/${tenantId}/pod-retry/run`, { method: 'POST', body: {} }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unbilled'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-draft'] });
      queryClient.invalidateQueries({ queryKey: ['worklist'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/**
 * Money earned and not collected, worst first — the pull half of what the
 * digests push to a phone.
 *
 * Nothing here re-derives a figure: core/worklist.py reads the unbilled
 * sweep and the receivables book and ranks them, and this hook only fetches
 * that answer. A console that re-sorted or re-totalled it would be a second
 * opinion about the same euros, and the two would drift the first time
 * either rule changed.
 */
export function useWorklist() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.worklist(tenantId),
    queryFn: () => api(`/api/tenants/${tenantId}/worklist`),
    enabled: Boolean(tenantId),
    // The same 30s the receivables book uses, and for the same reason: this
    // is built from it, so a longer window here would show a debt somebody
    // has already collected.
    staleTime: 30_000,
  });
}

export function useReceivables() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.receivables(tenantId),
    queryFn: () => api(`/api/tenants/${tenantId}/receivables`),
    enabled: Boolean(tenantId),
    // Short, not Infinity: overdue days tick over at midnight and every
    // figure here moves the moment anyone records a payment.
    staleTime: 30_000,
  });
}

/**
 * GET .../invoices/export/profiles — the column layouts the bookkeeper's
 * CSV can be written in, and the columns each one actually produces.
 *
 * Fetched rather than hard-coded in the panel, and that is the whole point
 * of the route existing. core/invoice_export.py's program presets are an
 * ASSUMPTION about which layout Rivilė, Finvalda, B1 and Būtenta each want —
 * nobody has run one through those programs — so the column list is shown
 * to the bookkeeper before they download, and `verified` decides whether
 * the caveat renders. A copy of that list in this bundle would be a second
 * statement of the layout that could disagree with the file, which is the
 * one thing a person checking a layout must not be handed.
 *
 * staleTime Infinity: it depends on no tenant data and changes only when
 * this product is redeployed.
 */
export function useInvoiceExportProfiles() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.invoiceExportProfiles(tenantId),
    queryFn: () => api(`/api/tenants/${tenantId}/invoices/export/profiles`),
    enabled: Boolean(tenantId),
    staleTime: Infinity,
  });
}

/**
 * POST /api/orders/reprice/preview — what a newly agreed rate table WOULD do
 * to this client's delivered, not-yet-invoiced loads.
 *
 * A MUTATION HOOK FOR A ROUTE THAT WRITES NOTHING, and the shape is
 * deliberate rather than a mistake. This is not a question the screen asks
 * on its own; it is a question a dispatcher asks by pressing "count it up",
 * with a client, a rate, a window and a material in hand. A useQuery here
 * would re-ask it on every keystroke of a half-typed rate and show a total
 * for 9,5 on the way to 9,50 — a money figure that was never asked for and
 * is briefly wrong.
 *
 * The server is a POST for its own reason (five parameters make an
 * unreadable query string); the two reasons happen to agree.
 */
export function usePreviewReprice() {
  return useMutation({
    mutationFn: (body) => api('/api/orders/reprice/preview', { method: 'POST', body }),
  });
}

/**
 * POST /api/orders/reprice — write the newly agreed rate onto every eligible
 * load.
 *
 * Invalidates the invoice caches because it moves what a draft would bill:
 * a draft cached from before the change is a proposal at the old rate, and
 * issuing from it is exactly the mistake this feature exists to prevent.
 *
 * `reason` is required by the server, in words, and is stored on every
 * order it touches — see blueprints/orders.apply_bulk_reprice().
 */
export function useApplyReprice() {
  const invalidate = useInvalidateInvoiceCaches();
  return useMutation({
    mutationFn: (body) => api('/api/orders/reprice', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

/**
 * GET .../invoices/compliance — numbering, VAT requisites and arithmetic
 * over the invoices issued in one period.
 *
 * NOT a compliance verdict, and the panel that renders it is careful to say
 * so; core/invoice_compliance.py's docstring holds the reasoning. An empty
 * findings list means "nothing found in the invoices this database holds",
 * which is a different sentence from "this month is compliant".
 *
 * `range` is {from, to} from monthRange(), or null while the month picker
 * holds something that is not a month — the query simply does not run then,
 * rather than asking the server about a range it would refuse.
 *
 * Under INVOICE_CACHES' `invoice-compliance` prefix, so cancelling an
 * invoice or issuing one re-asks: both change the numbering this answer is
 * about, and a stale "no findings" over a book that has just grown a gap is
 * the one wrong answer this panel must never show.
 */
export function useInvoiceCompliance(range) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.invoiceCompliance(tenantId, range?.from ?? null, range?.to ?? null),
    queryFn: () =>
      api(`/api/tenants/${tenantId}/invoices/compliance`
          + `?date_from=${range.from}&date_to=${range.to}`),
    enabled: Boolean(tenantId && range),
    staleTime: 30_000,
  });
}

/**
 * GET .../invoices/draft — what an invoice for this client WOULD contain,
 * and what it would leave out. Writes nothing.
 *
 * `until` is the server's cut-off on delivered_at and is INCLUSIVE: it is
 * the last delivery date to include, so '2026-08-31' closes August. The API
 * converts it to the exclusive bound core/invoicing.py wants
 * (blueprints/invoices.py:_delivered_before) rather than making a caller do
 * it. V-112 corrected this paragraph, which described the pre-fix server and
 * had a dispatcher typing 2026-09-01 to close August — which billed the 1st
 * of September into it. The empty string is dropped by qs() rather than sent
 * as `&until=`, which the server would reject.
 *
 * staleTime 0 on purpose. This is a proposal about money that another
 * dispatcher can invalidate by issuing first, and a cached draft is a list
 * of loads that may already be on somebody else's invoice.
 */
export function useInvoiceDraft(clientId, until) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.invoiceDraft(tenantId, clientId, until),
    queryFn: () =>
      api(`/api/tenants/${tenantId}/invoices/draft${qs({ client_id: clientId, until })}`),
    enabled: Boolean(tenantId && clientId),
    staleTime: 0,
  });
}

/**
 * GET /api/tenants/<id>/invoices — the issued documents, newest first,
 * filtered by status, searched by number or client, and PAGED.
 *
 * V-113. This used to take no parameters at all and the server answered
 * with a bare `LIMIT 200`. Since this list is the only place an invoice can
 * be marked paid, cancelled or downloaded, the two-hundred-and-first oldest
 * invoice was simply out of reach — while it went on being counted as debt
 * everywhere else. The filters are in the query key because they change the
 * answer; a cached page of "open" invoices is not a page of "paid" ones.
 */
export function useInvoices({ status = 'all', search = '', limit, offset } = {}) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.invoices(tenantId, { status, search, limit, offset }),
    queryFn: () =>
      api(`/api/tenants/${tenantId}/invoices${qs({ status, search, limit, offset })}`),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}

/**
 * GET /api/invoices/<id> — one invoice with the loads it covers.
 *
 * Only fetched when a row is expanded (`enabled`). The list endpoint
 * already carries every column the table renders; the loads are the answer
 * to "what did I actually bill them for", which is asked about one invoice
 * at a time and never about two hundred.
 */
export function useInvoice(invoiceId, { enabled = true } = {}) {
  return useQuery({
    queryKey: qk.invoice(invoiceId),
    queryFn: () => api(`/api/invoices/${invoiceId}`),
    enabled: Boolean(invoiceId) && enabled,
  });
}

/**
 * POST /api/tenants/<id>/invoices — issue.
 *
 * The client and the cut-off go up, never the lines: the server re-derives
 * them from the same function the draft used, so a load that lost its price
 * or was invoiced by somebody else between the draft and the button cannot
 * ride along on a stale list. See the route's own docstring.
 */
export function useIssueInvoice() {
  const tenantId = useTenantId();
  const invalidate = useInvalidateInvoiceCaches();
  return useMutation({
    mutationFn: ({ clientId, until }) =>
      api(`/api/tenants/${tenantId}/invoices`, {
        method: 'POST',
        body: { client_id: clientId, ...(until ? { until } : null) },
      }),
    onSuccess: invalidate,
  });
}

/**
 * POST /api/invoices/<id>/payment — record that somebody paid, or unrecord
 * it.
 *
 * `paid: false` is not an undo of a real transaction, it is the correction
 * of a mis-click. Nothing in this product has seen a bank statement, so a
 * payment mark is a bookkeeping note and has to be as reversible as one —
 * the alternative is a carrier whose receivables are permanently wrong
 * because somebody clicked the wrong row.
 */
export function useRecordPayment() {
  const invalidate = useInvalidateInvoiceCaches();
  return useMutation({
    mutationFn: ({ invoiceId, paid, note }) =>
      api(`/api/invoices/${invoiceId}/payment`, {
        method: 'POST',
        body: { paid, ...(note ? { note } : null) },
      }),
    onSuccess: invalidate,
  });
}

/**
 * POST /api/invoices/<id>/sent — record that the client actually got it.
 *
 * There is no email transport in this product: issuing renders a PDF, and
 * putting that PDF in front of the client happens in the carrier's own mail
 * client. This is the only place that act is written down, which is why an
 * invoice nobody marks stays on the worklist as money the client has never
 * been asked for.
 *
 * Clearable, exactly like the payment mark and for the same reason: a
 * mis-click on the wrong row must be correctable or the figure is wrong
 * forever.
 */
export function useRecordSent() {
  const invalidate = useInvalidateInvoiceCaches();
  return useMutation({
    mutationFn: ({ invoiceId, sent, note }) =>
      api(`/api/invoices/${invoiceId}/sent`, {
        method: 'POST',
        body: { sent, ...(note ? { note } : null) },
      }),
    onSuccess: invalidate,
  });
}

/**
 * POST /api/invoices/<id>/cancel — withdraw a document raised in error.
 *
 * Invalidating the draft matters more here than anywhere else in this file:
 * cancelling puts every covered load back to pod_generated, so the next
 * draft for that client is a different list than the one on screen.
 */
export function useCancelInvoice() {
  const invalidate = useInvalidateInvoiceCaches();
  return useMutation({
    mutationFn: ({ invoiceId, reason }) =>
      api(`/api/invoices/${invoiceId}/cancel`, {
        method: 'POST',
        body: { ...(reason ? { reason } : null) },
      }),
    onSuccess: invalidate,
  });
}
